// Player-level resource mutation. Single entry point for every read
// and write of a player's life, mana, and Death's Door immunity.
// Engine-side. GameBoard instantiates one `PlayerResources` per
// match with a thin host adapter that bridges to broadcast / HUD
// refresh / game-flow hooks.
//
// Why this exists:
//   - Resource writes used to be scattered across GameBoard.jsx (13+
//     mana sites, 3 life sites) with inconsistent clamping, partial
//     broadcasts, and three separate damage-routing implementations.
//   - Every future card that touches a player resource ("can't pay
//     mana", "mana costs N more", "lose N life", "heal N", "set
//     life to X") lands in ONE chokepoint so a fix lands once.
//   - Matches the established pattern of `EffectLedger`. An engine
//     class instantiated by GameBoard with a tiny adapter, not a JS
//     class that knows about React.
//
// Owns:
//   - Mana mutations (payMana / grantMana / setMana / bumpMana)
//   - Life mutations (loseLife / gainLife / dealAvatarDamage / applyLifeDelta)
//   - Death's Door immunity flag per player (cleared by GameBoard at
//     the controller's end-of-turn step)
//   - Read-only snapshot (mana + threshold) for affordability gates

import { TRACKER_DEFS } from '../../trackerConfig';
import { applyLifeLoss, applyHealing, isAtDeathsDoor, wouldBeDeathBlow } from './deathsDoor';

const MANA_MAX = TRACKER_DEFS.mana.max;

function clamp(lo, hi, v) {
  return Math.max(lo, Math.min(hi, v));
}

// ── Pure transforms ──────────────────────────────────────────────────
// Independently testable, no host needed. The instance methods below
// route through these so the tests can pin the arithmetic without
// stubbing the broadcast / refresh side effects.

/** Floor at 0; no upper cap (you can't pay yourself up). */
export function applyManaPayment(prev, amount) {
  return Math.max(0, (prev || 0) - Math.max(0, amount || 0));
}

/** Floor at 0, cap at MANA_MAX. */
export function applyManaGrant(prev, amount) {
  return clamp(0, MANA_MAX, (prev || 0) + Math.max(0, amount || 0));
}

/** Clamp the new value to [0, MANA_MAX]. Used for phase reset
 *  (mana = sites + ledger contribution) and sync. */
export function applyManaSet(value) {
  return clamp(0, MANA_MAX, Math.floor(Math.max(0, value || 0)));
}

// ── Class ─────────────────────────────────────────────────────────────

/**
 * @typedef {object} PlayerResourcesHost
 * @property {() => object} state                       Returns gameState (must include `trackers`).
 * @property {(player: string, key: string, value: number) => void} broadcastTracker
 * @property {() => void} refresh                       HUD refresh hook (e.g. updateTrackerTokenPositions + forceUpdate).
 *
 * Optional callbacks. Fired when the corresponding event happens.
 * The class never assumes a callback is present; missing callbacks
 * are no-ops.
 *
 * @property {() => boolean}                         [isGameOver]
 * @property {(player, amount, sourceLabel) => void} [onLifeLoss]            Imposter demask, log, etc.
 * @property {(player) => void}                      [onEnterDeathsDoor]     Log + toast (immunity flag is class-owned).
 * @property {(player, gained, sourceLabel) => void} [onHeal]
 * @property {(player, sourceLabel) => void}         [onHealFailed]
 * @property {(player, sourceLabel) => boolean}      [attemptDeathReplacement]  Returns true if the death is averted.
 * @property {(winner, reason) => void}              [declareGameOver]
 *
 * @property {(player, kind, amount) => boolean}     [emitResourceIntent]
 *   Server-authoritative cutover (P4). For an AUTOMATED flow (loseLife /
 *   gainLife / dealAvatarDamage / payMana / grantMana) the host may send a
 *   resource INTENT so the SERVER computes the value + owns the death's-door
 *   immunity / death-blow decision. Returning true means the host handled the
 *   authoritative sync, so the class SUPPRESSES its own absolute broadcast for
 *   that mutation (the opponent renders from the intent diff). Off the auth path
 *   it returns false and the legacy broadcast runs unchanged. Manual HUD nudges
 *   (setMana / bumpMana / setLife) NEVER fire this. They stay absolute SET_*.
 */

export class PlayerResources {
  /** @param {PlayerResourcesHost} host */
  constructor(host) {
    this.host = host;
    // Death's Door "can't die that turn" immunity per Sorcery codex.
    // DERIVED, not a mutable flag with a cleanup step: entering DD
    // stamps the turn it happened on (host.turnKey(). Synced state,
    // identical on both clients), and immunity holds exactly while the
    // current turn still matches the stamp. Expiry is automatic and
    // simultaneous on every client at the next turn boundary. The old
    // boolean flag was cleared by an end-phase step that ran only on
    // the passing player's client and only for the outgoing player.
    // the host's copy could stay immune forever, making combat unable
    // to ever land a death blow.
    this._ddEnteredTurnKey = { p1: null, p2: null };
  }

  // ── Reads ────────────────────────────────────────────────────────
  getMana(player) {
    return this._trackers()?.[player]?.mana || 0;
  }

  getLife(player) {
    return this._trackers()?.[player]?.life || 0;
  }

  isImmuneToDeathBlow(player) {
    const stamp = this._ddEnteredTurnKey[player];
    return stamp != null && stamp === this._turnKey();
  }

  /**
   * Stamp the moment a player reached Death's Door with the current
   * turn. Fired internally by loseLife; ALSO the entry point for the
   * tracker-mirror path. The non-acting client sees the life total
   * cross to zero as a bare tracker write, and must derive the same
   * immunity window or its own damage resolution (e.g. a spell it
   * casts later this turn) would death-blow prematurely.
   */
  noteDeathsDoorEntered(player) {
    this._ddEnteredTurnKey[player] = this._turnKey();
  }

  /** Survive reloads: a reconnect mid-turn must not forget that a
   *  player entered DD this very turn (premature death blow). */
  serializeDeathsDoor() {
    return { ...this._ddEnteredTurnKey };
  }

  restoreDeathsDoor(map) {
    if (!map) return;
    this._ddEnteredTurnKey = { p1: map.p1 ?? null, p2: map.p2 ?? null };
  }

  isAtDeathsDoor(player) {
    return isAtDeathsDoor(this.getLife(player));
  }

  /** Snapshot for affordability gating. Bundles mana + per-element
   *  threshold from the tracker. */
  snapshot(player) {
    const t = this._trackers()?.[player] || {};
    return {
      mana:  t.mana  || 0,
      air:   t.wind  || 0,
      earth: t.earth || 0,
      fire:  t.fire  || 0,
      water: t.water || 0,
    };
  }

  // ── Mana mutations ───────────────────────────────────────────────

  /**
   * Pay mana (e.g. cast cost, activation cost). Floors at 0. Paying
   * more than you have is allowed at the API level (other callers
   * gate affordability earlier); the floor just prevents negative
   * trackers.
   * @returns {number} amount actually deducted
   */
  payMana(player, amount) {
    return this._mutate(player, applyManaPayment, amount, 'subtract');
  }

  /**
   * Grant mana (e.g. GRANT_MANA op, host/online sync delta, end-of-
   * turn site reset). Caps at MANA_MAX.
   * @returns {number} amount actually added
   */
  grantMana(player, amount) {
    return this._mutate(player, applyManaGrant, amount, 'add');
  }

  /**
   * Set mana to an absolute value (phase recompute, full reset).
   * Clamps to [0, MANA_MAX].
   * @returns {boolean} true iff the value changed
   */
  setMana(player, value) {
    const trackers = this._trackers();
    if (!trackers || !trackers[player]) return false;
    const next = applyManaSet(value);
    if (next === trackers[player].mana) return false;
    trackers[player].mana = next;
    this.host.broadcastTracker(player, 'mana', next);
    this.host.refresh();
    return true;
  }

  /** Convenience: bump by ±1 (manual HUD nudge). This is a MANUAL override.
   *  per the P4 resource policy it stays an absolute SET_MANA broadcast, never a
   *  resource intent. So it flags the inner pay/grantMana to skip intent emit. */
  bumpMana(player, delta) {
    this._manualMutation = true;
    try {
      if (delta > 0) return this.grantMana(player, delta);
      if (delta < 0) return this.payMana(player, -delta);
      return 0;
    } finally {
      this._manualMutation = false;
    }
  }

  // ── Life mutations ───────────────────────────────────────────────

  /**
   * Lose life (any source. Site damage, pay-life cost, Witch curse,
   * Interrogator dodge). Floors at 0. Triggers Death's Door immunity
   * the moment life crosses from positive to zero. Does NOT death-
   * blow on its own; that's `dealAvatarDamage`'s job.
   * @returns {number} amount actually lost
   */
  loseLife(player, amount, sourceLabel) {
    if (this.host.isGameOver?.()) return 0;
    const trackers = this._trackers();
    if (!trackers || !trackers[player]) return 0;
    const prev = trackers[player].life || 0;
    const { nextLife, enteredDeathsDoor } = applyLifeLoss(prev, amount);
    if (nextLife === prev) return 0;
    trackers[player].life = nextLife;
    // Auth cutover: the server computes the arithmetic + owns the death's-door
    // stamp via the resource intent. Skip the absolute broadcast when handled.
    // `_suppressLifeIntent` is set by dealAvatarDamage when it already emitted a
    // single avatar-damage intent. The inner loseLife then only suppresses its
    // broadcast (the avatar intent owns the sync), never emits a second intent.
    const handled = this._suppressLifeIntent
      || this.host.emitResourceIntent?.(player, 'loseLife', amount);
    if (!handled) this.host.broadcastTracker(player, 'life', nextLife);
    this.host.refresh();
    const actual = prev - nextLife;
    this.host.onLifeLoss?.(player, actual, sourceLabel);
    if (enteredDeathsDoor) {
      this.noteDeathsDoorEntered(player);
      this.host.onEnterDeathsDoor?.(player);
    }
    return actual;
  }

  /**
   * Gain life (heal). Blocked at Death's Door per codex Healing.
   * Capped at the Sorcery max-life (20 by default).
   * @returns {number} amount actually gained
   */
  gainLife(player, amount, sourceLabel) {
    if (this.host.isGameOver?.()) return 0;
    const trackers = this._trackers();
    if (!trackers || !trackers[player]) return 0;
    const prev = trackers[player].life || 0;
    if (isAtDeathsDoor(prev)) {
      this.host.onHealFailed?.(player, sourceLabel);
      return 0;
    }
    const next = applyHealing(prev, amount);
    if (next === prev) return 0;
    trackers[player].life = next;
    if (!this.host.emitResourceIntent?.(player, 'gainLife', amount)) {
      this.host.broadcastTracker(player, 'life', next);
    }
    this.host.refresh();
    const gained = next - prev;
    this.host.onHeal?.(player, gained, sourceLabel);
    return gained;
  }

  /**
   * Direct Avatar damage. Differs from `loseLife` because it CAN be
   * a death blow per codex:
   *   - Not at DD                → routes to `loseLife`
   *   - At DD + immune this turn → routes to `loseLife` (life
   *                                already 0; no death blow yet)
   *   - At DD + not immune       → DEATH BLOW unless a player-level
   *                                WOULD_DIE replacement averts it
   *                                (Altar of Malachai)
   *
   * Site damage does NOT go through here per codex. It uses
   * `loseLife` directly so it never death-blows.
   *
   * Fires `onAvatarDamage` only when the Avatar actually takes damage
   * here. That hook is the Imposter demask trigger: the mask sheds on
   * Avatar damage, never on bare life loss (site hits, pay-life costs).
   */
  dealAvatarDamage(player, amount, sourceLabel) {
    if (this.host.isGameOver?.()) return 0;
    const life = this.getLife(player);
    if (wouldBeDeathBlow(life, this.isImmuneToDeathBlow(player))) {
      if (this.host.attemptDeathReplacement?.(player, sourceLabel)) return 0;
      // Auth cutover: the server owns the terminal death-blow decision (it
      // re-derives it from its own state via the dealAvatarDamage intent and
      // stamps `over`, which folds back as the authoritative game-over). The
      // local declareGameOver still runs for the acting client's immediate
      // freeze; the intent makes it authoritative + propagates to the opponent.
      this.host.emitResourceIntent?.(player, 'dealAvatarDamage', amount);
      const winner = player === 'p1' ? 'p2' : 'p1';
      this.host.declareGameOver?.(
        winner,
        `${sourceLabel || 'attack'} dealt a death blow to the Avatar`,
      );
      return 0;
    }
    // Not a death blow: emit ONE dealAvatarDamage intent (server stamps DD +
    // owns the future death-blow window), and suppress the inner loseLife's own
    // intent so the server sees a single avatar-damage write, not two.
    const handled = this.host.emitResourceIntent?.(player, 'dealAvatarDamage', amount);
    this._suppressLifeIntent = handled;
    let actual;
    try {
      actual = this.loseLife(player, amount, sourceLabel);
    } finally {
      this._suppressLifeIntent = false;
    }
    if (actual > 0) this.host.onAvatarDamage?.(player, actual, sourceLabel);
    return actual;
  }

  /**
   * Net signed delta. Positive heals, negative loses. Used by the
   * prompt-resolve aggregation step where a chain of effects produces
   * one combined life adjustment. Negative goes through `loseLife`
   * (always applies, floors at 0); positive through `gainLife`
   * (gated by Death's Door, capped at max).
   * @returns {number} signed delta actually applied
   */
  applyLifeDelta(player, delta, sourceLabel) {
    if (delta < 0) return -this.loseLife(player, -delta, sourceLabel);
    if (delta > 0) return this.gainLife(player, delta, sourceLabel);
    return 0;
  }

  /**
   * End-phase notice: fire the "no longer immune" log for any player
   * whose immunity ends with this turn. Does NOT null the stamps.
   * "can't die that turn" covers the entire turn including
   * after-each-turn triggers, so the actual expiry is left to the
   * derivation at the turn change. Safe to miss on a client (the end
   * phase runs only on the passing one): derivation expires the
   * immunity there regardless.
   */
  announceImmunityExpiry() {
    for (const p of ['p1', 'p2']) {
      if (this.isImmuneToDeathBlow(p)) this.host.onClearDeathsDoorImmunity?.(p);
    }
  }

  /** Force-expire a player's immunity (manual/test escape hatch.
   *  normal expiry is derived from the turn change). */
  clearDeathsDoorImmunity(player) {
    if (!this.isImmuneToDeathBlow(player)) return false;
    this._ddEnteredTurnKey[player] = null;
    this.host.onClearDeathsDoorImmunity?.(player);
    return true;
  }

  // ── Internal ─────────────────────────────────────────────────────

  // Identity of the current turn, synced across clients (currentTurn +
  // that player's turn counter). Hosts without the hook (focused unit
  // tests) get a constant. Immunity then persists until an explicit
  // clearDeathsDoorImmunity, matching the legacy behavior.
  _turnKey() {
    return this.host.turnKey?.() ?? '@static';
  }

  _trackers() {
    return this.host.state()?.trackers || null;
  }

  /**
   * Generic mutator. Computes next via the pure transform, writes the
   * tracker, broadcasts + refreshes only if the value changed.
   * Returns the absolute delta applied (always positive).
   */
  _mutate(player, transform, amount, direction) {
    if (!amount || amount <= 0) return 0;
    const trackers = this._trackers();
    if (!trackers || !trackers[player]) return 0;
    const prev = trackers[player].mana || 0;
    const next = transform(prev, amount);
    if (next === prev) return 0;
    trackers[player].mana = next;
    // Auth cutover: an AUTOMATED pay/grant emits a resource intent (server
    // computes the value). A MANUAL nudge (bumpMana) stays an absolute SET_MANA
    // broadcast per the P4 resource policy, so it skips the intent.
    const kind = direction === 'add' ? 'grantMana' : 'payMana';
    const handled = !this._manualMutation
      && this.host.emitResourceIntent?.(player, kind, amount);
    if (!handled) this.host.broadcastTracker(player, 'mana', next);
    this.host.refresh();
    return direction === 'add' ? next - prev : prev - next;
  }
}
